<script lang="ts">
  import { onMount } from 'svelte'
  import { api, type Category, type Domain } from '../lib/api'
  import { fmtDate } from '../lib/utils'
  import { notify } from '../lib/toast'
  import Modal from '../components/Modal.svelte'

  let cats = $state<Category[]>([])
  let doms = $state<Domain[]>([])
  let total = $state(0)
  let pages = $state(1)
  let loading = $state(false)

  let search = $state('')
  let catFilter = $state<number | ''>('')
  let page = $state(1)
  let limit = $state(50)
  let sort = $state<'name' | 'createdAt'>('name')
  let order = $state<'asc' | 'desc'>('asc')

  let addOpen = $state(false), editOpen = $state(false), bulkOpen = $state(false)
  let editing = $state<Domain | null>(null)

  let newName = $state(''), newCat = $state<number | ''>(''), newNotes = $state('')
  let bulkList = $state(''), bulkCat = $state<number | ''>('')
  let editName = $state(''), editCat = $state<number>(0), editNotes = $state(''), editEnabled = $state(true)

  let searchTimeout: any

  async function load() {
    loading = true
    try {
      const r = await api.listDomains({
        search: search || undefined,
        category: catFilter || undefined,
        page, limit, sort, order
      })
      doms = r.items
      total = r.total
      pages = r.pages
    } catch (e: any) {
      notify(e.message, 'err')
    } finally {
      loading = false
    }
  }

  function onSearchInput() {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => { page = 1; load() }, 250)
  }

  onMount(async () => {
    cats = await api.listCategories()
    await load()
  })

  let domainCats = $derived(cats)

  async function handleAdd() {
    if (!newName.trim()) return notify('Укажите домен', 'err')
    if (!newCat) return notify('Выберите категорию', 'err')
    try {
      await api.createDomain({ name: newName.trim().toLowerCase(), categoryId: Number(newCat), notes: newNotes || undefined })
      notify(`Добавлен ${newName}`, 'ok')
      addOpen = false; newName = ''; newNotes = ''
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleBulk() {
    if (!bulkCat) return notify('Выберите категорию', 'err')
    const names = bulkList.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean)
    if (!names.length) return
    try {
      const r = await api.bulkCreateDomain({ names, categoryId: Number(bulkCat) })
      notify(`+${r.added} дубли:${r.duplicate}${r.errors.length ? ` err:${r.errors.length}` : ''}`, 'ok')
      bulkOpen = false; bulkList = ''
      await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  function openEdit(d: Domain) {
    editing = d
    editName = d.name; editCat = d.categoryId; editNotes = d.notes || ''; editEnabled = d.enabled
    editOpen = true
  }

  async function handleEdit() {
    if (!editing) return
    try {
      await api.updateDomain(editing.id, { name: editName.trim().toLowerCase(), categoryId: editCat, notes: editNotes, enabled: editEnabled })
      notify('Сохранено', 'ok'); editOpen = false; await load()
    } catch (e: any) { notify(e.message, 'err') }
  }

  async function handleDelete(d: Domain) {
    if (!confirm(`Удалить ${d.name}?`)) return
    try { await api.deleteDomain(d.id); notify('Удалено', 'ok'); await load() } catch (e: any) { notify(e.message, 'err') }
  }

  async function deleteFromModal() {
    if (!editing) return
    await handleDelete(editing)
    editOpen = false
  }

  function changeSort(field: 'name' | 'createdAt') {
    if (sort === field) order = order === 'asc' ? 'desc' : 'asc'
    else { sort = field; order = 'asc' }
    load()
  }

  function goPage(p: number) {
    page = Math.max(1, Math.min(pages, p))
    load()
  }
</script>

<div class="card mb-4">
  <div class="flex flex-wrap gap-2 items-center">
    <input class="input flex-1 min-w-[200px]" placeholder="Поиск домена..." bind:value={search} oninput={onSearchInput}>
    <select class="input w-auto" bind:value={catFilter} onchange={() => { page = 1; load() }}>
      <option value="">Все категории</option>
      {#each domainCats as c}
        <option value={c.id}>{c.name} ({c._count?.domains || 0})</option>
      {/each}
    </select>
    <select class="input w-auto" bind:value={limit} onchange={() => { page = 1; load() }}>
      <option value={25}>25</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
      <option value={200}>200</option>
    </select>
    <button class="btn btn-primary" onclick={() => (addOpen = true)}>+ Домен</button>
    <button class="btn btn-secondary" onclick={() => (bulkOpen = true)}>+ Bulk</button>
    <span class="text-xs text-muted ml-auto">
      {#if loading}загрузка...{:else}
        {total} доменов · стр. {page}/{pages || 1}
      {/if}
    </span>
  </div>
</div>

<!-- Desktop: table -->
<div class="card overflow-hidden p-0 hidden sm:block">
  <table class="tbl">
    <thead>
      <tr>
        <th class="cursor-pointer select-none" onclick={() => changeSort('name')}>
          Домен {sort === 'name' ? (order === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th>Категория</th>
        <th>Notes</th>
        <th class="cursor-pointer select-none" onclick={() => changeSort('createdAt')}>
          Created {sort === 'createdAt' ? (order === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="w-[100px]">Действия</th>
      </tr>
    </thead>
    <tbody>
      {#each doms as d (d.id)}
        <tr class="cursor-pointer" onclick={() => openEdit(d)}>
          <td><code class="text-xs">{d.name}</code>{#if !d.enabled}<span class="badge badge-neutral ml-2">off</span>{/if}</td>
          <td class="text-xs">{d.category.name}</td>
          <td class="text-muted text-xs">{d.notes || ''}</td>
          <td class="text-muted text-xs">{fmtDate(d.createdAt)}</td>
          <td onclick={(e) => e.stopPropagation()}>
            <button class="btn-icon" onclick={() => openEdit(d)} title="Редактировать">✎</button>
            <button class="btn-icon btn-icon-danger" onclick={() => handleDelete(d)} title="Удалить">🗑</button>
          </td>
        </tr>
      {/each}
      {#if doms.length === 0}
        <tr><td colspan="5" class="text-center text-muted py-8">Ничего не найдено</td></tr>
      {/if}
    </tbody>
  </table>
</div>

<!-- Mobile: cards list -->
<div class="sm:hidden space-y-2">
  {#each doms as d (d.id)}
    <button class="w-full text-left card p-3 hover:border-accent/50 transition-colors" onclick={() => openEdit(d)}>
      <div class="flex justify-between items-start gap-2">
        <code class="text-xs font-mono break-all flex-1">{d.name}</code>
        {#if !d.enabled}<span class="badge badge-neutral">off</span>{/if}
      </div>
      <div class="flex justify-between items-center mt-2 text-xs text-muted">
        <span>{d.category.name}</span>
        <span>{fmtDate(d.createdAt)}</span>
      </div>
      {#if d.notes}<div class="text-xs text-muted mt-1">{d.notes}</div>{/if}
    </button>
  {/each}
  {#if doms.length === 0}
    <div class="text-center text-muted py-8 card">Ничего не найдено</div>
  {/if}
</div>

{#if pages > 1}
  <div class="flex items-center justify-center gap-1 mt-4">
    <button class="btn btn-secondary" disabled={page <= 1} onclick={() => goPage(1)}>«</button>
    <button class="btn btn-secondary" disabled={page <= 1} onclick={() => goPage(page - 1)}>‹</button>
    <span class="text-sm px-3">
      Стр. <b>{page}</b> из <b>{pages}</b>
    </span>
    <button class="btn btn-secondary" disabled={page >= pages} onclick={() => goPage(page + 1)}>›</button>
    <button class="btn btn-secondary" disabled={page >= pages} onclick={() => goPage(pages)}>»</button>
  </div>
{/if}

{#if addOpen}
  <Modal title="Добавить домен" onclose={() => (addOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Домен</label><input class="input" bind:value={newName} placeholder="example.com" autofocus></div>
      <div><label class="label">Категория</label>
        <select class="input" bind:value={newCat}>
          <option value="">—</option>
          {#each domainCats as c}<option value={c.id}>{c.name}</option>{/each}
        </select>
      </div>
      <div><label class="label">Notes (опц.)</label><input class="input" bind:value={newNotes}></div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (addOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleAdd}>Добавить</button>
    {/snippet}
  </Modal>
{/if}

{#if editOpen && editing}
  <Modal title="Редактировать домен" onclose={() => (editOpen = false)}>
    <div class="space-y-3">
      <div><label class="label">Домен</label><input class="input" bind:value={editName}></div>
      <div><label class="label">Категория</label>
        <select class="input" bind:value={editCat}>
          {#each domainCats as c}<option value={c.id}>{c.name}</option>{/each}
        </select>
      </div>
      <div><label class="label">Notes</label><input class="input" bind:value={editNotes}></div>
      <label class="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" class="checkbox" bind:checked={editEnabled}>
        <span class="text-sm">Включён (enabled)</span>
      </label>
    </div>
    {#snippet footer()}
      <button class="btn btn-danger mr-auto" onclick={deleteFromModal}>Удалить</button>
      <button class="btn btn-secondary" onclick={() => (editOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleEdit}>Сохранить</button>
    {/snippet}
  </Modal>
{/if}

{#if bulkOpen}
  <Modal title="Bulk импорт доменов" onclose={() => (bulkOpen = false)} wide>
    <div class="space-y-3">
      <div><label class="label">Категория</label>
        <select class="input" bind:value={bulkCat}>
          <option value="">—</option>
          {#each domainCats as c}<option value={c.id}>{c.name}</option>{/each}
        </select>
      </div>
      <div>
        <label class="label">Список (по одному на строку)</label>
        <textarea class="input font-mono text-xs h-48" bind:value={bulkList} placeholder="example.com&#10;another.com"></textarea>
      </div>
    </div>
    {#snippet footer()}
      <button class="btn btn-secondary" onclick={() => (bulkOpen = false)}>Отмена</button>
      <button class="btn btn-primary" onclick={handleBulk}>Импорт</button>
    {/snippet}
  </Modal>
{/if}
